interface PaginatedResponse<T> {
  results: T[]
  pagination: { page: number; pageSize: number; totalPages: number; totalCount: number }
}

interface FetchAllPagesOptions {
  maxConcurrency?: number  // 默认 3
  retries?: number         // 默认 1
  /**
   * Hard cap on pages fetched after the first. Prevents an accidental
   * unbounded fetch if the backend returns a huge totalPages. Default 50.
   */
  maxPages?: number
}

export async function fetchAllPages<T>(
  searchFn: (page: number) => Promise<PaginatedResponse<T>>,
  options?: FetchAllPagesOptions
): Promise<T[]> {
  const { maxConcurrency = 3, retries = 1, maxPages = 50 } = options || {}

  const firstPage = await searchFn(1)
  const allResults = [...firstPage.results]
  const { totalPages } = firstPage.pagination

  if (totalPages <= 1) return allResults

  const cappedTotal = Math.min(totalPages, maxPages)
  if (totalPages > maxPages) {
    console.warn(
      `[fetchAllPages] totalPages=${totalPages} exceeds maxPages=${maxPages}; truncating. ` +
      `Consider increasing pageSize or scoping the query more tightly.`
    )
  }
  const remainingPages = Array.from({ length: cappedTotal - 1 }, (_, i) => i + 2)

  for (let i = 0; i < remainingPages.length; i += maxConcurrency) {
    const batch = remainingPages.slice(i, i + maxConcurrency)
    const results = await Promise.allSettled(
      batch.map(page => fetchWithRetry(() => searchFn(page), retries))
    )
    for (const result of results) {
      if (result.status === 'fulfilled') {
        allResults.push(...result.value.results)
      } else {
        console.warn('[fetchAllPages] Failed to fetch page, skipping:', result.reason)
      }
    }
  }

  return allResults
}

async function fetchWithRetry<T>(fn: () => Promise<T>, retries: number): Promise<T> {
  for (let i = 0; i <= retries; i++) {
    try { return await fn() } catch (e) { if (i === retries) throw e }
  }
  throw new Error('unreachable')
}
